import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Zap, Building2, FileText, Users, BarChart3, HeadphonesIcon, RefreshCw,
  LogOut, Settings, ChevronLeft, ChevronRight, Home, Cpu
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/bluegov/dashboard', icon: Home },
  { name: 'Clientes', href: '/bluegov/clients', icon: Building2 },
  { name: 'Contratos', href: '/bluegov/contracts', icon: FileText },
  { name: 'Licenças', href: '/bluegov/licenses', icon: Users },
  { name: 'Métricas', href: '/bluegov/metrics', icon: BarChart3 },
  { name: 'Token Intelligence', href: '/bluegov/token-intelligence', icon: Cpu },
  { name: 'Suporte', href: '/bluegov/tickets', icon: HeadphonesIcon },
  { name: 'Renovações', href: '/bluegov/renewals', icon: RefreshCw },
];

export const BlueGovLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem('bluegov_user');
    const token = localStorage.getItem('bluegov_token');
    
    if (!token) {
      navigate('/bluegov/login');
      return;
    }

    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('bluegov_token');
    localStorage.removeItem('bluegov_user');
    navigate('/bluegov/login');
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <aside className={`${collapsed ? 'w-20' : 'w-64'} bg-slate-800 border-r border-slate-700 transition-all duration-300 flex flex-col`}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            {!collapsed && (
              <div>
                <h1 className="font-bold text-white">BlueGov</h1>
                <p className="text-xs text-slate-400">Admin SaaS</p>
              </div>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-slate-400 hover:text-white hover:bg-slate-700"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span className="font-medium">{item.name}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-slate-700">
          {user && !collapsed && (
            <div className="mb-3 px-3">
              <p className="text-sm font-medium text-white truncate">{user.name}</p>
              <p className="text-xs text-slate-400 truncate">{user.email}</p>
            </div>
          )}
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={`${collapsed ? 'w-10 p-0 justify-center' : 'w-full justify-start'} text-slate-400 hover:text-white hover:bg-slate-700`}
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="ml-3">Sair</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default BlueGovLayout;
